import React, { useContext, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { axios, baseUrl } from 'Api';
import { AuthContext } from 'Context';
import styles from './index.module.css';

const nameOf = (u) => `${u?.firstname || ''} ${u?.lastname || ''}`.trim()
  || u?.email?.split('@')[0] || '—';
const planOf = (u) => u?.subscription?.product_name || u?.plan_name || 'Free';

// Modal that grows out of the clicked plan card and lists the users created
// under that plan. `origin` is the clicked card's viewport rect (for the
// grow-from-card animation); `plan` carries the tile's name + badge.
const PlanUsersModal = ({ plan, origin, onClose }) => {
  const { getToken } = useContext(AuthContext);
  const [users, setUsers] = useState(null); // null = loading

  useEffect(() => {
    if (!plan) { setUsers(null); return undefined; }
    let alive = true;
    setUsers(null);
    (async () => {
      try {
        const token = await getToken();
        const res = await axios.post(`${baseUrl}/managerUsersList`, { token }).catch(() => null);
        const list = Array.isArray(res?.data?.data) ? res.data.data : [];
        const filtered = list.filter((u) => planOf(u) === plan.name);
        if (alive) setUsers(filtered);
      } catch (e) {
        if (alive) setUsers([]);
      }
    })();
    return () => { alive = false; };
  }, [plan, getToken]);

  // Offset the modal's start position/scale from the clicked card's centre so it
  // appears to grow out of it.
  const start = (() => {
    if (!origin || typeof window === 'undefined') return { opacity: 0, scale: 0.6 };
    const cx = origin.left + origin.width / 2;
    const cy = origin.top + origin.height / 2;
    return {
      opacity: 0,
      scale: 0.18,
      x: cx - window.innerWidth / 2,
      y: cy - window.innerHeight / 2,
    };
  })();

  return (
    <AnimatePresence>
      {plan && (
        <motion.div
          className={styles.modalOverlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className={styles.planModal}
            role="dialog"
            aria-modal="true"
            initial={start}
            animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
            exit={start}
            transition={{ type: 'spring', stiffness: 320, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.planModalHead}>
              <div>
                <span className={styles.planModalName}>{plan.name}</span>
                {plan.badge ? <span className={styles.planModalBadge}>{plan.badge}</span> : null}
              </div>
              <button type="button" className={styles.planModalClose} onClick={onClose} aria-label="Close">
                <CloseRoundedIcon fontSize="small" />
              </button>
            </div>
            <span className={styles.planModalSub}>
              {users === null
                ? 'Loading users…'
                : `${users.length} ${users.length === 1 ? 'user' : 'users'} created on this plan`}
            </span>

            <div className={styles.planModalList}>
              {users === null ? (
                [0, 1, 2].map((i) => (
                  <div key={i} className={styles.planModalRow}>
                    <span className={`${styles.planModalAvatar} ${styles.planModalSkeleton}`} />
                    <span className={`${styles.planModalSkeletonText} ${styles.planModalSkeleton}`} />
                  </div>
                ))
              ) : users.length === 0 ? (
                <div className={styles.planModalEmpty}>No users created on this plan yet.</div>
              ) : (
                users.map((u, idx) => {
                  const name = nameOf(u);
                  const image = u?.dpURL || u?.image;
                  return (
                    <div key={u?.userId || u?.email || idx} className={styles.planModalRow}>
                      {image ? (
                        <img className={styles.planModalAvatar} src={image} alt={name} />
                      ) : (
                        <span className={styles.planModalAvatar}>{name.charAt(0).toUpperCase()}</span>
                      )}
                      <span className={styles.planModalUser}>
                        <span className={styles.planModalUserName}>{name}</span>
                        <span className={styles.planModalUserEmail}>{u?.email}</span>
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PlanUsersModal;
